import React from "react";
import { Search, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface SearchInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Show a spinner instead of the clear button */
  isLoading?: boolean;
  /** If provided, shows a clear (X) button when the input has a value */
  onClear?: () => void;
  containerClassName?: string;
}

/**
 * Shared pill-style search input.
 * Matches the Pings search bar design: rounded-full, muted background,
 * transparent border that transitions in on focus.
 */
export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    { className, containerClassName, isLoading, onClear, value, ...props },
    ref
  ) => {
    const hasValue = value !== undefined && value !== "";

    return (
      <div className={cn("relative", containerClassName)}>
        <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/70" />
        <Input
          ref={ref}
          value={value}
          className={cn(
            "z-body h-10 rounded-full border-border/40 bg-muted pl-11 shadow-none",
            "transition-[background-color,border-color,box-shadow]",
            "placeholder:text-muted-foreground/90",
            "focus-visible:border-border/80 focus-visible:bg-background focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-0",
            hasValue || isLoading ? "pr-10" : "pr-4",
            className
          )}
          {...props}
        />
        {isLoading ? (
          <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground/60" />
          </span>
        ) : hasValue && onClear ? (
          <button
            type="button"
            tabIndex={-1}
            onClick={onClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>
    );
  }
);

SearchInput.displayName = "SearchInput";
